import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Checkbox, FormControlLabel, Grid, Stack, TextField, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { DialogModal } from "@zfp-akademie-a-s/zfp-design";
import { FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { AddIcon, DeleteIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { type LifestyleExpenseFormType, getLifestyleExpenseFormSchema } from "Forms/Schemas/Budget/Expenses/lifestyleExpenseFormSchema";
import {
  getGetBudgetOverviewQueryKey,
  getGetLifeStyleExpenseQueryKey,
  useGetLifeStyleExpense,
  useReplaceLifeStyleExpense,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type LifeStyleAmount, LifeStyleAmountAmountType, LifeStyleExpenseEntryMode } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { Fragment, type RefObject, forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import type { ExpenseFormHandle } from "../Components/EditExpenseDrawer";

type Props = {
  onClose: () => void;
  ref?: RefObject<ExpenseFormHandle>;
};

export const LifestyleExpenseForm = forwardRef<ExpenseFormHandle, Props>(({ onClose }, ref) => {
  const { readOnly } = useContext(AnalysisDetailContext);
  const { t } = useResource();
  const [detailed, setDetailed] = useState(true);
  const [openDeleteBulkModal, setOpenDeleteBulkModal] = useState<boolean>(false);
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const queryClient = useQueryClient();
  const { data: lifestyleExpenseData } = useGetLifeStyleExpense(analysisId, versionId);
  const { mutateAsync: replaceLifeStyleExpenseAsync } = useReplaceLifeStyleExpense();

  const { control, setValue, watch, handleSubmit, clearErrors } = useForm<LifestyleExpenseFormType>({
    resolver: yupResolver(getLifestyleExpenseFormSchema(t)),
    mode: "onBlur",
    defaultValues: {
      aggregatedAmount: (lifestyleExpenseData?.aggregatedAmount?.value.toString() as any) || "",
      customItems: [],
      detailed: true,
    },
  });

  const {
    fields: customFields,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: "customItems",
  });

  const customItems = watch("customItems");
  const vacation = watch("vacation");
  const hobbies = watch("hobbies");
  const entertainment = watch("entertainment");
  const sport = watch("sport");
  const health = watch("health");

  useEffect(() => {
    if (detailed) {
      const predefinedFieldsSum =
        (Number.parseFloat(vacation ?? "0") || 0) +
        (Number.parseFloat(hobbies ?? "0") || 0) +
        (Number.parseFloat(entertainment ?? "0") || 0) +
        (Number.parseFloat(sport ?? "0") || 0) +
        (Number.parseFloat(health ?? "0") || 0);

      const customItemsSum =
        customItems?.reduce((sum, item) => {
          const value = Number.parseFloat(item.value ?? "0") || 0;
          return sum + value;
        }, 0) || 0;

      const total = predefinedFieldsSum + customItemsSum;
      setValue("aggregatedAmount", total.toString());
    }
  }, [customItems, vacation, hobbies, entertainment, sport, health, detailed, setValue]);

  const recalculateTotal = () => {
    if (detailed) {
      const predefinedFieldsSum =
        (Number.parseFloat(vacation ?? "0") || 0) +
        (Number.parseFloat(hobbies ?? "0") || 0) +
        (Number.parseFloat(entertainment ?? "0") || 0) +
        (Number.parseFloat(sport ?? "0") || 0) +
        (Number.parseFloat(health ?? "0") || 0);

      const customItemsSum =
        customItems?.reduce((sum, item) => {
          const value = Number.parseFloat(item.value ?? "0") || 0;
          return sum + value;
        }, 0) || 0;

      const total = predefinedFieldsSum + customItemsSum;
      setValue("aggregatedAmount", total.toString());
    }
  };

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(onSubmit)();
    },
    deleteExpense: () => {
      setOpenDeleteBulkModal(true);
    },
  }));

  useEffect(() => {
    if (lifestyleExpenseData) {
      handleChangeDetailed(lifestyleExpenseData.entryMode === LifeStyleExpenseEntryMode.individual);
      setValue("aggregatedAmount", (lifestyleExpenseData.aggregatedAmount?.value ?? 0).toString());

      const customItems: { name: string; value: string }[] = [];
      for (const expense of lifestyleExpenseData.amounts) {
        switch (expense.amountType) {
          case LifeStyleAmountAmountType.vacation:
            setValue("vacation", expense.amount.value.toString());
            break;
          case LifeStyleAmountAmountType.hobbies:
            setValue("hobbies", expense.amount.value.toString());
            break;
          case LifeStyleAmountAmountType.entertainment:
            setValue("entertainment", expense.amount.value.toString());
            break;
          case LifeStyleAmountAmountType.sport:
            setValue("sport", expense.amount.value.toString());
            break;
          case LifeStyleAmountAmountType.health:
            setValue("health", expense.amount.value.toString());
            break;
          case LifeStyleAmountAmountType.other:
            customItems.push({
              name: expense.name,
              value: expense.amount.value.toString(),
            });
            break;
        }
      }
      replace(customItems);
    }
  }, [lifestyleExpenseData, setValue, replace]);

  const onSubmit = async (data: LifestyleExpenseFormType) => {
    if (Number(data.aggregatedAmount) === 0) {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseAmountZeroError));
      return;
    }

    const customItems: LifeStyleAmount[] =
      data.customItems?.map((field) => ({
        amountType: LifeStyleAmountAmountType.other,
        amount: {
          value: Number.parseFloat(field.value ?? "0") || 0,
          currencyCode: "CZK",
          frequency: AmountFrequency.monthly,
        },
        name: field.name ?? "",
      })) ?? [];

    const amounts: LifeStyleAmount[] = detailed
      ? [
          {
            amountType: LifeStyleAmountAmountType.vacation,
            amount: {
              value: Number.parseFloat(data.vacation ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.LifeStyleExpenseTypes.Vacation),
          },
          {
            amountType: LifeStyleAmountAmountType.hobbies,
            amount: {
              value: Number.parseFloat(data.hobbies ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.LifeStyleExpenseTypes.Hobbies),
          },
          {
            amountType: LifeStyleAmountAmountType.entertainment,
            amount: {
              value: Number.parseFloat(data.entertainment ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.LifeStyleExpenseTypes.Entertainment),
          },
          {
            amountType: LifeStyleAmountAmountType.sport,
            amount: {
              value: Number.parseFloat(data.sport ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.LifeStyleExpenseTypes.Sport),
          },
          {
            amountType: LifeStyleAmountAmountType.health,
            amount: {
              value: Number.parseFloat(data.health ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.LifeStyleExpenseTypes.Health),
          },
          ...customItems,
        ]
      : [];

    try {
      await replaceLifeStyleExpenseAsync({
        analysisId,
        versionId,
        data: {
          entryMode: detailed ? LifeStyleExpenseEntryMode.individual : LifeStyleExpenseEntryMode.aggregate,
          aggregatedAmount: {
            value: Number.parseFloat(data.aggregatedAmount ?? "0") || 0,
            currencyCode: "CZK",
            frequency: AmountFrequency.monthly,
          },
          amounts,
        },
      });

      onClose();
    } catch {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
    } finally {
      queryClient.invalidateQueries({ queryKey: getGetLifeStyleExpenseQueryKey(analysisId, versionId) });
      queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
    }
  };

  const onDelete = async () => {
    try {
      await replaceLifeStyleExpenseAsync({
        analysisId,
        versionId,
        data: {
          entryMode: detailed ? LifeStyleExpenseEntryMode.individual : LifeStyleExpenseEntryMode.aggregate,
          aggregatedAmount: { value: 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
          amounts: [],
        },
      });

      queryClient.invalidateQueries({ queryKey: getGetLifeStyleExpenseQueryKey(analysisId, versionId) });
      queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
      onClose();
    } catch {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
    }
  };

  const onDeleteBulkExpense = () => {
    replace([]);
    setOpenDeleteBulkModal(false);
    onDelete();
  };

  const handleChangeDetailed = (value: boolean) => {
    setDetailed(value);
    setValue("detailed", value);
    clearErrors();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={Spacing.XXL} pb={Spacing.XXL} sx={{ height: "100%" }}>
        <Box>
          <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
            {t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.LifeStyle)}
          </Typography>

          <Grid container spacing={Spacing.XL}>
            <Grid size={6}>
              <FormNumberField
                control={control}
                name="aggregatedAmount"
                label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Fields.Aggregated)}
                valueType="text"
                scale={2}
                min={0}
                thousandsSeparator=" "
                unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                disabled={detailed || readOnly}
                placeholder="0"
                fullWidth
              />
            </Grid>

            <Grid size={6}>
              <Box display="flex" alignItems="flex-start" mt={5} height="100%">
                <FormControlLabel
                  control={<Checkbox checked={detailed} disabled={readOnly} onChange={(_, checked) => handleChangeDetailed(checked)} />}
                  label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Fields.DetailedCheckbox)}
                />
              </Box>
            </Grid>

            {detailed && (
              <>
                <Grid size={6}>
                  <TextField
                    label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.Item)}
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.LifeStyleExpenseTypes.Vacation)}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="vacation"
                    label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.Value)}
                    valueType="text"
                    scale={2}
                    min={0}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    disabled={readOnly}
                    placeholder="0"
                    onChange={recalculateTotal}
                    fullWidth
                  />
                </Grid>

                <Grid size={6}>
                  <TextField
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.LifeStyleExpenseTypes.Hobbies)}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="hobbies"
                    valueType="text"
                    scale={2}
                    min={0}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    disabled={readOnly}
                    placeholder="0"
                    onChange={recalculateTotal}
                    fullWidth
                  />
                </Grid>

                <Grid size={6}>
                  <TextField
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.LifeStyleExpenseTypes.Entertainment)}
                    disabled
                    fullWidth
                  />
                </Grid>

                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="entertainment"
                    valueType="text"
                    scale={2}
                    min={0}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    disabled={readOnly}
                    placeholder="0"
                    onChange={recalculateTotal}
                    fullWidth
                  />
                </Grid>

                <Grid size={6}>
                  <TextField
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.LifeStyleExpenseTypes.Sport)}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="sport"
                    valueType="text"
                    scale={2}
                    min={0}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    disabled={readOnly}
                    placeholder="0"
                    onChange={recalculateTotal}
                    fullWidth
                  />
                </Grid>

                <Grid size={6}>
                  <TextField
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.LifeStyleExpenseTypes.Health)}
                    disabled
                    fullWidth
                  />
                </Grid>

                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="health"
                    valueType="text"
                    scale={2}
                    min={0}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    disabled={readOnly}
                    placeholder="0"
                    onChange={recalculateTotal}
                    fullWidth
                  />
                </Grid>

                {customFields.map((field, idx) => (
                  <Fragment key={field.id}>
                    <Grid size={6}>
                      <FormTextField
                        control={control}
                        name={`customItems.${idx}.name`}
                        disabled={readOnly}
                        placeholder={t(Resources.AnalysisDetail.Budget.Expenses.Form.Placehodlers.OwnName)}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={5}>
                      <FormNumberField
                        control={control}
                        name={`customItems.${idx}.value`}
                        valueType="text"
                        scale={2}
                        min={0}
                        thousandsSeparator=" "
                        unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                        disabled={readOnly}
                        placeholder="0"
                        onChange={recalculateTotal}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={1} display="flex" alignItems="flex-start" justifyContent="center">
                      <Button disabled={readOnly} variant="text" color="error" onClick={() => remove(idx)}>
                        <DeleteIcon />
                      </Button>
                    </Grid>
                  </Fragment>
                ))}

                <Grid size={12}>
                  <Button variant="text" disabled={readOnly} startIcon={<AddIcon />} onClick={() => append({ name: "", value: "" })}>
                    {t(Resources.AnalysisDetail.Budget.Expenses.Form.Buttons.AddCustomItem)}
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        <DialogModal
          open={openDeleteBulkModal}
          onClose={() => setOpenDeleteBulkModal(false)}
          closeLabel={t(Resources.Common.Close)}
          title={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpense)}
          subtitle={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpenseHint)}
          primaryAction={
            <Button variant="contained" disabled={readOnly} onClick={onDeleteBulkExpense}>
              {t(Resources.Common.Yes)}
            </Button>
          }
        />
      </Stack>
    </form>
  );
});
